import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Images
import heroImage from "@/assets/paver-patio-repair-totowa-passaic-county-new-jersey-2.webp";
import rutherfordImage from "@/assets/paver-repair-rutherford-nj.webp";
import bloomingdaleImage from "@/assets/weeds-paver-repair-bloomingdale-nj.webp";
import wanaqueImage from "@/assets/paver-repair-polymeric-sand-wanaque-nj.webp";

// Citation link component - wraps text with citation number
const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-secondary hover:underline"
  >
    {children}<sup>[{num}]</sup>
  </a>
);

const faqs = [
  {
    question: "How do I know if my North Jersey paver patio needs repair or replacement?",
    answer: "If the base is still intact and less than 30 percent of your pavers are damaged, repair is usually the better option. We check for drainage issues, base failure, and how widespread the damage is. If problems are limited to a section or two, there is no reason to tear out the whole thing.",
  },
  {
    question: "Can paver repairs be done during winter in North Jersey?",
    answer: "Spring and fall are the ideal seasons for most paver work. Polymeric sand needs at least 24 hours above 32 degrees to cure properly. We can do some repairs in winter if conditions allow, but joint work usually has to wait for warmer weather.",
  },
  {
    question: "How long does a typical paver patio repair take?",
    answer: "Most residential repairs take one to two days depending on the scope. A small sunken section might be done in a few hours. Larger jobs with base work and resanding take longer. We give you a timeline before we start.",
  },
  {
    question: "Will resanding fix pavers that have already shifted?",
    answer: "No, resanding alone will not fix shifting. The base has to be repaired first. Once we lift the pavers, fix the gravel and sand layers underneath, and relay everything level, then we resand the joints. Skipping the base work means the shifting comes back.",
  },
  {
    question: "Do I need a permit for paver patio repair in North Jersey?",
    answer: "Most repairs do not require permits. If you are expanding the patio footprint or making changes that affect drainage, some municipalities want to see a permit. We can help you figure out what your town requires.",
  },
  {
    question: "How soon after repair can I use my patio?",
    answer: "Light foot traffic is fine after 24 hours. Hold off on placing furniture or heavy items for 48 to 72 hours to let everything fully set. If we used polymeric sand, the cure time matters even more.",
  },
];

const citations = [
  {
    num: 1,
    text: "Interlocking Concrete Pavement Institute. \"Tech Spec 2: Construction of Interlocking Concrete Pavements.\" ICPI, Revised February 2020.",
  },
  {
    num: 2,
    text: "Interlocking Concrete Pavement Institute. \"Tech Spec 5: Cleaning, Sealing and Joint Sand Stabilization of Interlocking Concrete Pavement.\" ICPI, Revised February 2012.",
  },
  {
    num: 3,
    text: "Concrete Masonry & Hardscapes Association. \"Cleaning, Sealing and Joint Sand Stabilization of Interlocking Concrete Pavement.\" CMHA Tech Note PAV-TEC-005.",
  },
  {
    num: 4,
    text: "ASTM International. \"ASTM C936/C936M-21a: Standard Specification for Solid Concrete Interlocking Paving Units.\" ASTM, 2021.",
  },
];

export default function PaverPatioRepair() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Paver Patio Repair Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Paver patio sinking or shifting? We fix base failures, resand joints, and seal surfaces for North Jersey homeowners. Call Masonry Pro for a free assessment." />
        <meta property="og:description" content="Paver patio sinking or shifting? We fix base failures, resand joints, and seal surfaces for North Jersey homeowners. Call Masonry Pro for a free assessment." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Paver Patio Repair",
            description: "Paver patio repair in North Jersey. Fix base failures, resand joints, and seal surfaces.",
            url: "/paving-services/paver-patio-repair",
            image: "https://masonry-pro.com/og/paver-patio-repair.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/paver-patio-repair.jpg" />
        <meta property="og:title" content="Paver Patio Repair Totowa, NJ | Masonry Pro Construction" />
        <meta name="twitter:title" content="Paver Patio Repair in Totowa, NJ | Masonry Pro Construction" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/paver-patio-repair.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section - Centered with lighter overlay */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Paver patio repair in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,16,23,0.45), rgba(13,16,23,0.65))' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Local Paver Patio Repair in <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span> Serving North Jersey
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 1.5rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Re-leveling sunken and shifting pavers so your patio holds up through our winters
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="patio-restoration" packageName="Patio Restoration Package" blurb={"Lift and re-level the field, replace polymeric sand, repair failed sections, and seal the surface. All bundled at a fraction of the cost of a new install."} />

          {/* Intro Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg">
                <p className="text-lg text-muted-foreground">
                  Do you know that sinking feeling when you step onto your patio and notice the pavers aren't level anymore? Our North New Jersey winters do a number on outdoor surfaces. Water gets into the joints, freezes, expands, and before you know it, you've got sections that wobble or pool water after every rain.
                </p>
                <p className="text-lg text-muted-foreground mt-4">
                  Most of the sunken patios we get called out to in Totowa, Woodland Park, and Wayne are not failing because of the pavers. The pavers are usually fine. What gave way is the base underneath, either because it was too thin to begin with or because water found a path through it and carried the fines out. This means we will pull the affected section, rebuild the base, and reset the same pavers where we can, which is a lot less expensive than tearing out the whole patio.
                </p>
                <p className="text-lg text-muted-foreground mt-4">
                  We handle all types of paver patio repairs. Sunken spots, loose joints, cracked units, and drainage that is no longer working. If you're in <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, or <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> County, we can usually get out for an assessment within the same week. Give us a call or text and we'll take a look.
                </p>
              </div>
            </div>
          </section>

          {/* Sunken and Shifting Pavers - Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={rutherfordImage} 
                    alt="Sinking patio pavers cracking and heaving and ready for paver repair in Rutherford, Bergen County, New Jersey" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Sunken and Shifting Pavers <span className="text-gradient-gold">Signal Base Failure</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      You know how a mattress gets that dip in the middle after a few years where you always sleep? Your patio base does something similar, except instead of your body weight, it is dealing with thousands of pounds of frozen water pushing up from below every winter.
                    </p>
                    <p>
                      We work all over Essex and Bergen counties, and let me tell you, the clay soil around here is no friend to patios. It soaks up water like a sponge. Then, come January, all that trapped moisture turns to ice and the ground swells up in random spots. Then it thaws out, drops back down, then freezes again. Your pavers are just along for the ride, and after enough of these cycles they end up tilted, sunken, or rocking like a loose tooth.
                    </p>
                    <p>
                      Most folks think their pavers have gone bad when they notice the wobble. But if you pull one up and look at it, most likely its fine. The gravel and sand underneath though? That is where things have gone sideways. Literally, sometimes. We have opened up patios where the base layer looked like it had been stirred with a giant spoon.
                    </p>
                    <p>
                      <CitationLink num={1} url="https://icpi.org">Industry guidelines call for a minimum base thickness of 4 inches after compaction</CitationLink> for patios over well-drained soils, with additional depth required for wet or weak soil conditions. One thing that still surprises people is how fast the damage spreads once it starts. Water is lazy and always heads for the lowest point, which makes low spots lower and the problem bigger each season.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Resanding Joints - Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Resanding Joints Stops Weeds and <span className="text-gradient-gold">Restores Stability</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      The joint sand is not decorative. It locks your pavers together so they act like one solid surface instead of a bunch of loose bricks. Once it washes out, everything shifts around, and the gaps widen. Then the weeds can find a home, and then the ants move in, making it worse.
                    </p>
                    <p>
                      North Jersey springs are brutal on paver joints. All that rain we get in April and May flushes sand out little by little. Most people don't even notice until the gaps are big enough to catch a heel.
                    </p>
                    <p>
                      We clean out whatever is left in there, pull the weeds and roots, then pack fresh sand in tight and run a compactor over everything. It usually takes a day for most patios. Your surface will feel solid again, and the weeds lose their foothold.
                    </p>
                    <p>
                      Depending on how much rain and foot traffic your patio gets, resanding should happen every couple of years. Skip it too long, and you end up needing more than just sand.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={bloomingdaleImage} 
                    alt="Weeds growing between interlocked paver stones needing repair in Bloomingdale, Passaic County, New Jersey, 07403" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Polymeric Sand - Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={wanaqueImage} 
                    alt="Broom spreading polymeric sand for paver repair in Wanaque, Passaic County, New Jersey" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Polymeric Sand <span className="text-gradient-gold">Outlasts Regular Sand</span> in Harsh Weather
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Can you use regular sand in your paver joints? You can, however, when a big rainstorm rolls through, a lot of the sand will wash out into your yard. Or the ants get into it and start nesting.
                    </p>
                    <p>
                      Polymeric sand has polymers mixed in to allow it to harden when it gets wet. The joints basically turn solid, and water runs right over instead of washing anything away. Ants cannot dig through it, and weeds struggle to take hold. <CitationLink num={2} url="https://icpi.org">Using stabilized sand has advantages in areas prone to joint sand loss</CitationLink>.
                    </p>
                    <p>
                      We use polymeric on most jobs now, especially around Paramus and Wayne where the road salt runoff is heavy. Salt can eat through regular sand faster than people realize.
                    </p>
                    <p>
                      Polymeric does cost more. Maybe three times the cost of regular sand. But when you do the math, regular sand could need to be replaced every year. With Polymeric, five to seven years is pretty typical before the joints need work again.
                    </p>
                    <p>
                      There is a catch, though. It needs 24 hours with the temperature at 32 degrees or higher to cure. We cannot install it when rain is coming either. So we stick to late spring and early fall for these jobs when the weather behaves. We can discuss the best options for you when we take a look.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Overlay Installation - Text Only */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Overlay Installation Works When <span className="text-gradient-gold">Existing Base Is Solid</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    What if you hate how your patio looks, but the base underneath is still in good shape? Tearing everything out seems like a waste.
                  </p>
                  <p>
                    An overlay means we install new pavers right on top of your existing surface. No demolition. No hauling off tons of old material. The new layer bonds to what is already there.
                  </p>
                  <p>
                    We do a lot of these in Ridgewood and Montclair. Older homes with patios from the 80s or 90s. The style is dated, but the base never failed. Homeowners want something fresh without the mess and cost of starting from scratch.
                  </p>
                  <p>
                    Not every patio qualifies, though. We have to check a few things first. Is the surface draining properly, or does water pool in spots? Are there sections that have sunk or heaved? Any cracks running through the concrete underneath?
                  </p>
                  <p>
                    If the answer to all of that is no, overlay could work. If there are drainage problems or soft spots, putting new pavers on top just hides the issue for a year or two. Then you are dealing with the same problems again, plus now you have two layers to remove.
                  </p>
                  <p>
                    We will be straight with you about which way makes sense. Sometimes overlay saves you thousands. Sometimes it is money wasted. It all depends entirely on what is going on underneath.
                  </p>
                </div>
              </div>
            </div>
          </section>


          {/* Professional Repairs - Text Only */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Professional Repairs Extend Patio Life by <span className="text-gradient-gold">15+ Years</span>
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    If you are in Wayne or Paramus and staring at a patio that has seen better days, you are probably wondering whether to fix it or tear it out and start over. The answer depends on what is actually wrong with it.
                  </p>
                  <p>
                    A patio with some sunken spots, washed out joints, and a few cracked pavers? That is almost always repairable. We can lift the problem areas, rebuild the base with proper gravel and compaction, relay the pavers, and fill the joints with polymeric sand. When its done right, a repair can add another 15 years or more to the life of your patio.
                  </p>
                  <p>
                    Now if the whole surface is heaving, drainage is failing everywhere, and half the pavers are cracked or chipped, a <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">new paver patio</Link> starts to make more sense. Repairs only go so far when the original installation was not done correctly. <CitationLink num={4} url="https://www.astm.org/c0936_c0936m-21a.html">Quality pavers are manufactured to resist at least 50 freeze-thaw cycles with minimal surface loss</CitationLink>, so if the pavers themselves are still in good shape, the issue is usually the base.
                  </p>
                  <p>
                    Here is something else to consider. North Jersey has codes about drainage grades. Water has to flow away from your foundation, not toward it. A lot of older patios were installed before these rules. When we do repairs, we make sure everything meets current standards, protects your home, and avoids issues if you ever sell.
                  </p>
                  <p>
                    We are not going to push you toward a bigger job if you do not need it. Some contractors will do that. We would rather fix what can be fixed and save you the money. If replacement really is the better option, we will explain why.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <YouTubeShortsSection 
            title="See Our"
            highlightedTitle="Paver Patio Transformations"
            videoIds={["L6zFeqCTTSI", "QMzipttnOXM", "awvHabWeQFw"]}
          />

          <RelatedServices currentCategory="paving" />

          {/* CTA Section */}
          <CTASection 
            heading="Need a" 
            highlightedHeading="Patio Paver Repair?"
            serviceName="Paver Patio Repair"
          />

          {/* FAQ Section - Card Grid */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="bg-background">
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Citations Accordion */}
          <section className="py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations">
                    <AccordionTrigger className="text-lg font-semibold">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 text-sm text-muted-foreground">
                        {citations.map((citation) => (
                          <p key={citation.num}>
                            [{citation.num}] {citation.text}
                          </p>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          {/* Hub Backlink */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-lg text-muted-foreground">
                <p>
                  Patio repair is one of our <Link to="/paving-services" className="text-secondary hover:underline">paving services in North Jersey</Link>, and we handle new installs too.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-primary">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-primary-foreground">
                Ready to Restore Your Patio?
              </h2>
              <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
                Call or text us for a free assessment. We'll take a look and give you an honest recommendation.
              </p>
              <Button variant="gold" size="lg" asChild>
                <a href="tel:+19737252395">Call or Text (973) 725-2395</a>
              </Button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
